import { useCallback, useMemo, useState } from 'react'
import { Box, Button } from '@mui/material'
import moment from 'moment'
import { useAtomValue, useSetAtom } from 'jotai'
import * as sx from '../../common/stylesInline/report_daily_branch'
import { Item } from '../../common/snippets'
import Datepicker from '../../common/datePicker'
import { ReportHeading } from './common/ReportHeading'
import { GetRequest } from '../../common/api'
import { urls, xlCol, xlColq } from '../../common/constants'
import useToken from '../../helpers/useTokens'
import { companySettingsAtom } from '../../common/jotaiState/commonStateAtoms'
import { alertAtom, globalLoaderAtom } from '../../common/jotaiState/globalStateAtoms'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from '../../../i18n/I18nProvider'

const emptyGeneralSettings = {}

const openViewReport = (reportPayload) => {
  const rid = Date.now().toString()

  if (!window.generatedReports) {
    window.generatedReports = {}
  }

  window.generatedReports[rid] = reportPayload
  window.open(`/view-report?rid=${rid}`, '_blank')
}

const WhatsAppMessageConsumption = () => {
  const { translations } = useTranslation()
  const gs = translations?.generalSettings || emptyGeneralSettings
  const tr = useCallback((key, fallback) => gs?.[key] || fallback, [gs])
  const [, setSearchParams] = useSearchParams()
  const { loginData } = useToken()
  const { intCompanyID } = loginData || {}
  const companySettings = useAtomValue(companySettingsAtom)
  const { WhatsappTypeID } = companySettings || {}
  const setAlert = useSetAtom(alertAtom)
  const setLoader = useSetAtom(globalLoaderAtom)

  const [fromDate, setFromDate] = useState(moment(new Date()))
  const [toDate, setToDate] = useState(moment(new Date()))
  const reportName = tr('waMsgConsumptionReport', 'WhatsApp Message Consumption Report')
  const criteriaHeaders = useMemo(
    () => [
      { header: tr('waSentFromDate', 'Sent From Date'), field: 'FromDate', xlCol: { dataType: 'string' } },
      { header: tr('waSentToDate', 'Sent To Date'), field: 'ToDate', xlCol: { dataType: 'string' } },
    ],
    [tr]
  )
  const WhatsAppConsumptionHeaders = useMemo(() => {
    const header = (key, fallback, field, column, weight) => {
      const label = tr(key, fallback)
      return { header: label, exportHead: label, field, xlCol: column, weight }
    }

    return [
      header('sn', 'SN', 'SN', xlCol, 8),
      header('waTransactionDate', 'Transaction Date', 'Date', xlCol, 18),
      header('waEntryType', 'Entry Type', 'DRCR_Type', xlCol, 15),
      header('waMessageCount', 'Message Count', 'SentCount', xlColq, 18),
      header('waSessionCount', 'Session Count', 'SessionCount', xlColq, 18),
      header('waOpeningBalance', 'Opening Balance', 'OpeningBalance', xlColq, 20),
      header('waTxnAmount', 'Txn Amount', 'Amount', xlColq, 18),
      header('waClosingBalance', 'Closing Balance', 'ClosingBalance', xlColq, 20),
      header('waTransactionRemarks', 'Transaction Remarks', 'Remarks', xlCol, 25),
    ]
  }, [tr])

  const criteriaData = useMemo(
    () => ({
      FromDate: moment(fromDate).format('DD-MMM-YYYY'),
      ToDate: moment(toDate).format('DD-MMM-YYYY'),
      dataType: 'entry',
    }),
    [fromDate, toDate]
  )
  const params = {
    view: 'General Settings',
    tab: 521,
  }
  const goToTabs = () => {
    setSearchParams(params)
  }
  const handleGenerateReport = async () => {
    const dateDifference = Math.abs(toDate.diff(fromDate, 'days'))

    if (dateDifference > 31) {
      setAlert({ show: true, msg: tr('waReportMax31Days', 'This report cannot be taken for more than 31 days'), nextId: '' })
      return
    }

    setLoader(true)

    try {
      const response = await GetRequest(urls.whatsappTransactionsReport, {
        CompanyID: intCompanyID,
        WhatsappType: WhatsappTypeID || 0,
        FromDate: moment(fromDate).format('YYYY-MM-DD'),
        ToDate: moment(toDate).format('YYYY-MM-DD'),
      })

      if (response?.status !== true) {
        setAlert({ show: true, msg: response?.ErrMessage || response?.Message || tr('requestFailed', 'Request Failed'), nextId: '' })
        return
      }
      if (response.Data.Table.length === 0) {
        setAlert({ show: true, msg: tr('noDataFound', 'No Data Found'), nextId: '' })
        return
      }

      const tableData = response.Data.Table.map((row, index) => {
        return {
          SN: index + 1,
          ...row,
          dataType: 'entry',
        }
      })
      openViewReport({
        reportName,
        criteriaHeaders,
        criteriaData: [criteriaData],
        tableData,
        mainHeader: WhatsAppConsumptionHeaders,
      })
    } catch (error) {
      setAlert({ show: true, msg: error.message || tr('requestFailed', 'Request Failed'), nextId: '' })
    } finally {
      setLoader(false)
    }
  }

  return (
    <Item sx={{ padding: '0' }} className="reports">
      <ReportHeading reportName={tr('waMsgConsumption', 'WhatsApp Message Consumption')} />
      <Box sx={{ ...sx.reportFlex, justifyContent: 'start', paddingTop: '16px !important', gap: '12px' }}>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }} className="input--container bookingDatePicker">
          <Datepicker
            label={tr('waSentFromDate', 'Sent From Date')}
            name="whatsappMessageConsumptionFromDate"
            id="whatsappMessageConsumptionFromDate"
            value={fromDate}
            endDate={toDate}
            dateChange={setFromDate}
            width="100%"
          />
        </Item>
        <Item sx={{ ...sx.itemsx, minWidth: '230px' }} className="input--container bookingDatePicker">
          <Datepicker
            label={tr('waSentToDate', 'Sent To Date')}
            name="whatsappMessageConsumptionToDate"
            id="whatsappMessageConsumptionToDate"
            value={toDate}
            startDate={fromDate}
            dateChange={setToDate}
            width="100%"
          />
        </Item>
        <Item sx={sx.itemsx}>
          <Button
            variant="contained"
            id="whatsappCommunicationLoadButton"
            style={{ ...sx.fullHeight, ...sx.novp }}
            className="btn btn-primary"
            onClick={handleGenerateReport}
          >
            {tr('gsLoad', 'LOAD')}
          </Button>
        </Item>
        <Item sx={sx.itemsx}>
          <Button style={{ ...sx.fullHeight, ...sx.novp }} className="btn btn-primary" variant="outlined" onClick={() => goToTabs()}>
            {tr('waBackToPolicy', 'Back to Policy')}
          </Button>
        </Item>
      </Box>
    </Item>
  )
}

export default WhatsAppMessageConsumption
